import { DataVersion, ZapierActionPayload } from '@hakimo-ui/hakimo/types';
import {
  getEnvironmentFromHostname,
  withDataVersion,
} from '@hakimo-ui/hakimo/util';
import { useMemo } from 'react';
import useAuthenticatedMutation from '../shared/useAuthenticatedMutation';
/**
 * A data-access hook that provides a mutation function to send an action to the backend Zapier proxy.
 *
 * @param onSuccess - Optional callback for a successful mutation.
 * @param onError - Optional callback for a mutation error.
 * @returns A mutation object from `useAuthenticatedMutation`.
 */
export function useSendActionToZapier(
  onSuccess?: () => void,
  onError?: (err: Error) => void,
  dataVersion?: DataVersion
) {
  const ZAPIER_PROXY_URL = '/v2/orm/location_incidents/zapier';
  const environmentVal = getEnvironmentFromHostname();
  const requestTemplate = useMemo(() => {
    const req = new Request(ZAPIER_PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    return withDataVersion(req, dataVersion);
  }, [dataVersion]);
  // Getting the original mutation
  const mutation = useAuthenticatedMutation<null, ZapierActionPayload>(
    requestTemplate,
    {
      onSuccess,
      onError,
    }
  );
  // Wrap mutate to inject environment
  const mutateWithEnv = (payload: ZapierActionPayload) =>
    mutation.mutate({ ...payload, environment: environmentVal });
  return { ...mutation, mutate: mutateWithEnv };
}

export default useSendActionToZapier;
